/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ROUTES from '@src/constants/route';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import queryString from 'query-string';
import camelcaseKeys from 'camelcase-keys';
import {
  DURATION,
  PACK_DURATION,
  PACKAGE_CHANGE_TYPES,
  PACKAGE_CODE_V3,
  PACKAGE_EXPIRED,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import i18n from '@src/languages';
import apis from '@src/apis';
import { EVENT_TYPE } from '@src/constants';
import StackedAvatars from '@src/components/StackedAvatars';
import { CRM_URL } from '@src/configs';
import { max, min } from 'lodash';
import { checkVietNam } from '@src/utils/checkCountry';
import { COLOR } from '@src/styles/color';
import {
  calculateCharacterPurchase,
  getPackageChangeType,
  getPackageCurrentPrice,
  checkStudioCustomPackage,
  getTotalPricePackage,
} from '@src/services/package';
import avatarMP11 from '@src/assets/avatars/avatar-mp-1-1.png';
import avatarMP12 from '@src/assets/avatars/avatar-mp-1-2.png';
import avatarMP13 from '@src/assets/avatars/avatar-mp-1-3.png';
import avatarMP21 from '@src/assets/avatars/avatar-mp-2-1.png';
import avatarMP22 from '@src/assets/avatars/avatar-mp-2-2.png';
import avatarMP23 from '@src/assets/avatars/avatar-mp-2-3.png';
import avatarMP31 from '@src/assets/avatars/avatar-mp-3-1.png';
import avatarMP32 from '@src/assets/avatars/avatar-mp-3-2.png';
import avatarMP33 from '@src/assets/avatars/avatar-mp-3-3.png';
import { getSavedPriceBasePrice } from '@src/services/voucher';
import { PAYMENT_CURRENCY_UNIT } from '@src/constants/payment';
import {
  StyledNewPackageItem,
  StyledPackageItemWrapper,
} from '../../index.style';
import CreditsSlider from '../../Slider';
import PackageHeader from '../../SubPackage/PackageHeader';

const formatNumber = (num, language = 'vi') => {
  const format = (value) =>
    `${value.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })}K`;

  if (num >= 1e3) return format(num / 1e3);
  return num.toString();
};

const BestPackItem = ({
  packages,
  pkg,
  saleOffPercentage,
  monthlyPriceWithSaleOff,
  correspondMonthPrice,
  totalPrice,
  isMobile,
  duration,
  hasDiscountPackage,
  canUseVouchers = [],
  packageTopUp,
  currentUsingPackage,
  setPackages,
  correspondMonthPkg,
  bestPackageSuggestion,
  setBestPackageSuggestion,
}) => {
  const { t } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const location = useLocation();
  const { voucherCode, bannerType } = camelcaseKeys(
    queryString.parse(location.search) || {},
  );
  const { user = {} } = useSelector((state) => state.auth);
  const { usingPackage } = useSelector((state) => state.user);
  const userRef = useRef(user);
  const [bestVoucher, setBestVoucher] = useState();
  const [highlight, setHighlight] = useState(false);
  const [isDiscountPackage, setIsDiscountPackage] = useState(false);
  const [totalRemainingPrice, setTotalRemainingPrice] = useState(0);

  // Change credit option
  const maxNumberOptionCredit = useMemo(
    () =>
      max(pkg?.cycle?.creditOptions?.map((item) => item.creditsPerCycle) || []),
    [pkg?.code],
  );

  const minNumberOptionCredit = useMemo(
    () =>
      min(pkg?.cycle?.creditOptions?.map((item) => item.creditsPerCycle) || []),
    [pkg?.code],
  );

  const [currentCredits, setCurrentCredits] = useState(pkg?.creditsPerCycle);

  useEffect(() => {
    setCurrentCredits(pkg?.creditsPerCycle);
  }, [bestPackageSuggestion]);

  const handleChangeListPackages = (newValue) => {
    const optionCredit = pkg?.cycle?.creditOptions?.find(
      (opt) => opt?.creditsPerCycle === newValue,
    );
    if (!optionCredit) return;

    const updatedPackage = {
      ...pkg,
      price: optionCredit.vndPrice,
      usdPrice: optionCredit.usdPrice,
      monthlyPrice: optionCredit.vndMonthlyPrice,
      usdMonthlyPrice: optionCredit.usdMonthlyPrice,
      autoRenew: {
        active: pkg?.autoRenew?.active,
        vndPrice: optionCredit.autoRenewVndPrice,
        usdPrice: pkg?.autoRenew?.usdPrice,
      },
      cycle: {
        ...pkg?.cycle,
        creditsPerCycle: optionCredit.creditsPerCycle,
      },
    };

    let updatedCorrespondMonthPkg = null;
    if (duration === DURATION.YEARLY) {
      const optionCreditMonth = correspondMonthPkg?.cycle?.creditOptions?.find(
        (opt) => opt?.creditsPerCycle === newValue,
      );

      if (optionCreditMonth) {
        updatedCorrespondMonthPkg = {
          ...correspondMonthPkg,
          price: optionCreditMonth.vndPrice,
          usdPrice: optionCreditMonth.usdPrice,
          monthlyPrice: optionCreditMonth.vndMonthlyPrice,
          usdMonthlyPrice: optionCreditMonth.usdMonthlyPrice,
        };
      }
    }

    setPackages((prev) => {
      const filtered = prev.filter(
        (p) => p?.code !== pkg?.code && p?.code !== correspondMonthPkg?.code,
      );

      return [
        ...filtered,
        updatedPackage,
        ...(updatedCorrespondMonthPkg ? [updatedCorrespondMonthPkg] : []),
      ];
    });
  };

  const handleChangeCredits = (_, newValue, manualChange = false) => {
    if (newValue > maxNumberOptionCredit || newValue < minNumberOptionCredit) {
      return;
    }
    setCurrentCredits(newValue);
    if (bestPackageSuggestion?.bestVoucher && manualChange) {
      setBestPackageSuggestion((prev) => {
        const { bestVoucher: __, ...rest } = prev;
        return { ...rest };
      });
    }
    handleChangeListPackages(newValue);
  };

  useEffect(() => {
    handleChangeCredits(null, currentCredits);
  }, [currentCredits]);

  const marks = [
    {
      value: 0,
      label: '',
    },
    ...(pkg?.cycle?.creditOptions?.map((option) => ({
      value: option?.creditsPerCycle,
      label: t('remainChars', {
        numberCharacters: formatNumber(option?.creditsPerCycle, language),
      }),
    })) || []),
    {
      value: maxNumberOptionCredit + maxNumberOptionCredit,
      label: '',
    },
  ];

  const [
    remainingValueOfTheCurrentPackage,
    setRemainingValueOfTheCurrentPackage,
  ] = useState({ price: 0, usdPrice: 0 });
  const isCustomPackage = checkStudioCustomPackage(
    currentUsingPackage?.type,
    currentUsingPackage?.level,
  );

  const isVietNam = checkVietNam();

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const isExpiredPackage = checkExpiredDate(user?.packageExpiryDate);

  const { price, usdPrice } = getPackageCurrentPrice(pkg, true);
  const isDiscountUsingVoucher = bestVoucher?.value;

  const showPackageTotalPrice = duration !== DURATION.MONTHLY && price !== 0;

  const getBestVoucherForUser = () => {
    if (bestPackageSuggestion?.bestVoucher) {
      setBestVoucher(bestPackageSuggestion?.bestVoucher);
      return;
    }
    // Check if not using free package or not have voucher to use or not api enterprise year
    if (!canUseVouchers || canUseVouchers.length === 0) {
      setBestVoucher();
      return;
    }

    if (canUseVouchers.length > 0) {
      setBestVoucher(canUseVouchers[0]);
    }
  };

  const minCreditTopup = packageTopUp?.creditPurchaseOptions?.sort(
    (a, b) => a - b,
  )[0];

  const { customPrice, customUsdPrice } = calculateCharacterPurchase(
    minCreditTopup,
    packageTopUp,
  );

  const apiUsingPackage = packages?.find(
    (pack) => pack?.code === user?.apiUsageOptions?.code,
  );
  const isUpgradePackage = useMemo(() => {
    const determinePackageChange = () => {
      if (!usingPackage) return null;
      return getPackageChangeType({
        currPackage: pkg,
        prevPackage: apiUsingPackage || usingPackage,
        prevPackageExpiredDate: apiUsingPackage
          ? user?.apiPackage?.packageExpiryDate
          : user?.packageExpiryDate,
      });
    };
    return determinePackageChange() === PACKAGE_CHANGE_TYPES.UPGRADE;
  }, [pkg, usingPackage, apiUsingPackage, user]);

  const minPriceTopup = isVietNam ? customPrice : customUsdPrice;
  const sentEventAddToCartMoEngage = (packageCode, paymentUrl) => {
    const [product, packageItem, period] = pkg.code.split('-');

    const isTopUp = packageCode === packageTopUp?.code;
    const hasRemainingValue = isUpgradePackage || isCustomPackage;
    let finalPrice;
    if (hasRemainingValue) {
      finalPrice = totalRemainingPrice;
    } else if (showPackageTotalPrice) {
      finalPrice = totalPrice;
    } else {
      finalPrice = price;
    }

    const addToCartEventData = {
      event: EVENT_TYPE.ADD_TO_CART,
      userId: user?.id,
      email: user.email,
      package_code: packageCode,
      product,
      plan: packageItem,
      period,
      voucher_type: bestVoucher?.type,
      voucher_code: bestVoucher?.code,
      voucher_amount: bestVoucher?.value,
      voucher_title: isVietNam
        ? bestVoucher?.title?.vi
        : bestVoucher?.title?.en,
      package_price: isVietNam ? pkg.price : pkg.usdPrice,
      price: finalPrice,
      ...(hasRemainingValue
        ? { remaining_package_value: remainingValueOfTheCurrentPackage?.price }
        : {}),
      isTopUp,
      // Set undefined to avoid overriding data from other event cases
      min_credit_topup: undefined,
      min_price_topup: undefined,
      url: paymentUrl,
      currency_unit: isVietNam
        ? PAYMENT_CURRENCY_UNIT.VND
        : PAYMENT_CURRENCY_UNIT.USD,
    };

    const addToCartTopUpEventData = {
      event: EVENT_TYPE.ADD_TO_CART,
      userId: user?.id,
      email: user.email,
      product,
      package_code: packageCode,
      // Set undefined to avoid overriding data from other event cases
      period: undefined,
      voucher_type: undefined,
      voucher_code: undefined,
      voucher_amount: undefined,
      package_price: undefined,
      isTopUp,
      min_credit_topup: minCreditTopup,
      min_price_topup: minPriceTopup,
      url: paymentUrl,
      currency_unit: isVietNam
        ? PAYMENT_CURRENCY_UNIT.VND
        : PAYMENT_CURRENCY_UNIT.USD,
    };
    window.dataLayer.push(
      isTopUp ? addToCartTopUpEventData : addToCartEventData,
    );
  };

  const handleBuyPackage = async () => {
    const packageCode = pkg?.code;

    const buildSearchParams = (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value);
        }
      });
      return `?${searchParams.toString()}`;
    };

    const baseParams = {
      voucherCode: bestVoucher?.code,
      package: packageCode,
      creditsPerCycle: pkg?.cycle ? pkg?.cycle?.creditsPerCycle : undefined,
    };

    const search = voucherCode
      ? buildSearchParams({
          ...baseParams,
          voucherCode,
          bannerType,
          packageCode: pkg?.code,
        })
      : buildSearchParams({
          ...baseParams,
        });
    const moEngagePaymentUrl = `${CRM_URL}${ROUTES.PAYMENT}${search}`;
    sentEventAddToCartMoEngage(packageCode, moEngagePaymentUrl);

    history.push(`${ROUTES.PAYMENT}${search}`);
  };

  const renderButtonTextV3 = () => {
    const isUsingV3Package = Object.values(PACKAGE_CODE_V3)?.includes(
      usingPackage?.code,
    );

    const hasSamePackage =
      usingPackage?.expiresIn === pkg?.expiresIn &&
      usingPackage?.level === pkg?.level;
    if (
      hasSamePackage ||
      isUpgradePackage ||
      isExpiredPackage ||
      !isUsingV3Package
    )
      return t('buyNow');

    return t('buyPackageBtnTitle');
  };

  const renderPackagePriceUnit = (value) => {
    if (!value) return '';
    if (value < 1) return `/${t('character')}`;
    return `/${t('month')}`;
  };

  const renderBilledTotalDesc = (packageDuration) => {
    switch (packageDuration) {
      case PACK_DURATION.MONTH:
        return t('billedMonthly');
      case PACK_DURATION.YEAR:
      case PACKAGE_EXPIRED.YEARLY:
        return t('perYear');
      default:
        return '';
    }
  };

  const checkDisplayBuyButton = (studioPackages) =>
    pkg.price > 0 || !studioPackages?.id;
  const displayBuyButton = checkDisplayBuyButton(usingPackage);

  const renderPrice = (priceValue) => {
    let formattedPrice =
      monthlyPriceWithSaleOff > 1
        ? monthlyPriceWithSaleOff?.toLocaleString(language)
        : priceValue?.toLocaleString(language);

    formattedPrice =
      monthlyPriceWithSaleOff?.toLocaleString(language) ||
      priceValue?.toLocaleString(language);

    if (!isVietNam && formattedPrice !== '0') {
      if (duration !== DURATION.MONTHLY) {
        formattedPrice = (totalPrice / 12)?.toLocaleString(language, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } else {
        const number = +Number(formattedPrice.replace(',', '.'));
        formattedPrice = number.toLocaleString(language, {
          minimumFractionDigits: 2,
        });
      }
    }

    const renderInternationPrice = () => {
      if (isVietNam)
        return (
          <>
            <span className="three-first">
              {/* Format before 4 last character is big */}
              {formattedPrice.slice(0, -4) || '0'}
            </span>
            {/* Format last 4 character is small */}
            <span className="three-last">
              {formattedPrice.slice(-4) === '0' ? '' : formattedPrice.slice(-4)}
            </span>
          </>
        );
      return formattedPrice;
    };

    return (
      <>
        <Typography
          className={classnames(
            isDiscountPackage || pkg.mostPopular ? 'price-highlight' : 'price',
            {
              'price-en': !isVietNam && priceValue > 0,
            },
            {
              'unit-price-vip': pkg?.level === PACKAGE_LEVEL.VIP,
            },
          )}
        >
          <span
            className={`${
              isDiscountPackage || pkg.mostPopular
                ? 'unit-price-highlight'
                : 'unit-price'
            } ${isVietNam ? 'underlined' : ''}`}
          >
            {isVietNam ? 'đ' : '$'}
          </span>
          {/* PSUGO pakage have month price < 1 */}
          {renderInternationPrice()}
        </Typography>
      </>
    );
  };

  const renderTotalPrice = (value) => (
    <>
      <Typography
        className={classnames('original-price', {
          'price-en': !isVietNam && value > 0,
        })}
      >
        {!isVietNam && (
          <span className="original-price-unit font-size-14">$</span>
        )}
        {isVietNam && (
          <span className="original-price-unit underlined font-size-14">đ</span>
        )}
        {value}
      </Typography>
      <Typography className="sub-origin-price">
        {renderBilledTotalDesc(pkg.expiresIn)}
      </Typography>
    </>
  );

  useEffect(() => {
    const { startAt, endAt } = pkg.discountConfig || {};
    const inDiscountTime = startAt && moment().isBetween(startAt, endAt);
    if (inDiscountTime || pkg.mostPopular) {
      setHighlight(true);
    } else setHighlight(false);

    if (inDiscountTime) setIsDiscountPackage(true);
    else setIsDiscountPackage(false);
  }, [pkg, hasDiscountPackage]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    getBestVoucherForUser();
  }, [canUseVouchers, isDiscountPackage, bestPackageSuggestion]);

  const getAndSetRemainingValueOfTheCurrentPackage = async (packageType) => {
    const data = await apis.orders.remainingValueOfTheCurrentPackage({
      packageType,
    });

    if (data?.status) setRemainingValueOfTheCurrentPackage(data?.result);
  };

  useEffect(() => {
    // Get remaining value of the current package when using pricing model V3, not buy topup characters, upgrade package
    const checkConditionGetRemainingValue = isUpgradePackage || isCustomPackage;

    if (checkConditionGetRemainingValue) {
      getAndSetRemainingValueOfTheCurrentPackage(PACKAGE_TYPE.STUDIO);
    }
  }, [isUpgradePackage, location]);

  useEffect(() => {
    const checkConditionGetRemainingValue = isUpgradePackage || isCustomPackage;

    if (checkConditionGetRemainingValue && remainingValueOfTheCurrentPackage) {
      const basePrice = price || pkg?.price;
      const baseUsdPrice = usdPrice || pkg?.usdPrice;

      const { price: remainingPrice = 0, usdPrice: remainingUsdPrice = 0 } =
        remainingValueOfTheCurrentPackage || {};

      const adjustedPrice = Math.max(0, basePrice - remainingPrice);
      const adjustedUsdPrice = Math.max(0, baseUsdPrice - remainingUsdPrice);

      const savedPrice = getSavedPriceBasePrice({
        voucher: bestVoucher,
        currPrice: adjustedPrice,
        currUsdPrice: adjustedUsdPrice,
      });

      const adjustedTotalPrice = getTotalPricePackage({
        voucher: bestVoucher,
        currPrice: adjustedPrice,
        currUsdPrice: adjustedUsdPrice,
        savedPrice,
      });

      setTotalRemainingPrice(adjustedTotalPrice);
    }
  }, [
    remainingValueOfTheCurrentPackage,
    bestVoucher,
    pkg,
    price,
    duration,
    isUpgradePackage,
    isCustomPackage,
  ]);

  const PackagePrice = () => (
    <>
      <div className="price-wrapper">
        {/* Monthly price with sale off */}
        <div
          className={classnames('price-package', {
            'price-package-vip': pkg?.level === PACKAGE_LEVEL.VIP,
          })}
        >
          {isVietNam ? renderPrice(price) : renderPrice(usdPrice)}
        </div>
        {/* Monthly original price */}
        {(saleOffPercentage || isDiscountPackage || isDiscountUsingVoucher) && (
          <div className="price-original-discount">
            <Typography>
              {!isVietNam && (
                <span className="discount-unit-price font-size-14">$</span>
              )}
              {isVietNam && (
                <span className="discount-unit-price underlined font-size-14">
                  đ
                </span>
              )}
              <span className="original-price-month">
                {correspondMonthPrice?.toLocaleString(language)}
              </span>
            </Typography>
          </div>
        )}
        {/* Unit */}
        <span className="original-price-not-line-through">
          &nbsp;{renderPackagePriceUnit(pkg.price)}
        </span>
      </div>
      {showPackageTotalPrice && (
        <div className="original-price-wrapper">
          {renderTotalPrice(totalPrice?.toLocaleString(language))}
        </div>
      )}
      {!isMobile && !showPackageTotalPrice && (
        <div className="original-price-wrapper" />
      )}
    </>
  );

  const renderRandomAvatars = () => {
    const avatarsDay1List = [avatarMP11, avatarMP12, avatarMP13];
    const avatarsDay2List = [avatarMP21, avatarMP22, avatarMP23];
    const avatarsDay3List = [avatarMP31, avatarMP32, avatarMP33];
    const allAvatars = [avatarsDay1List, avatarsDay2List, avatarsDay3List];

    const currentDate = new Date();
    const dayOfWeek = currentDate.getDay();
    return allAvatars[dayOfWeek % allAvatars.length];
  };

  const buttonBuyPackageView = () => (
    <Button
      fullWidth
      className={classnames('button-start', {
        'button-highlight': highlight && pkg.mostPopular,
        'button-package-vip': pkg?.level === PACKAGE_LEVEL.VIP,
        'button-slider-credits': !pkg?.cycle,
      })}
      variant={highlight && pkg.mostPopular ? 'contained' : 'outlined'}
      data-id="add-to-cart"
      data-product={pkg?.type}
      data-package-code={pkg?.code}
      data-pricing-duration={duration}
      data-button-text={renderButtonTextV3()}
      onClick={() => handleBuyPackage(null)}
      disabled={!displayBuyButton && !isExpiredPackage}
    >
      {renderButtonTextV3()}
    </Button>
  );

  return (
    <>
      <StyledPackageItemWrapper
        isPoppular={pkg.mostPopular}
        isMobile={isMobile}
      >
        <StyledNewPackageItem
          isVipPack={pkg?.level === PACKAGE_LEVEL.VIP}
          isPoppular={pkg.mostPopular}
          isMobile={isMobile}
          highlight={highlight}
          isDiscountPackage={
            isDiscountPackage || (pkg.mostPopular && !hasDiscountPackage)
          }
          isVietNam={isVietNam}
          isDisableButton={!displayBuyButton && !isExpiredPackage}
          className={pkg?.level === PACKAGE_LEVEL.VIP && 'package-vip'}
        >
          <div className="card">
            <div className="card-package" role="button" tabIndex="0">
              {/** Package Header */}
              <PackageHeader
                pkg={pkg}
                isDiscountPackage={isDiscountPackage}
                bestVoucher={bestVoucher}
              />

              {/** Package Price */}
              <PackagePrice />

              {pkg?.cycle?.creditOptions?.length > 0 && marks?.length > 0 && (
                <CreditsSlider
                  maxNumberOptionCredit={maxNumberOptionCredit}
                  minNumberOptionCredit={minNumberOptionCredit}
                  marks={marks}
                  currentCredits={currentCredits}
                  handleChangeCredits={handleChangeCredits}
                />
              )}

              {/** Button buy package */}
              <div className="card-content">
                {buttonBuyPackageView()}
                {pkg.mostPopular ? (
                  <div
                    style={{
                      marginBottom: 0,
                    }}
                    className="popularity"
                  >
                    <StackedAvatars images={renderRandomAvatars(false)} />
                    <Typography
                      className="popularity-text"
                      variant="body2"
                      sx={{ color: COLOR.black[64] }}
                    >
                      {t('mostPeopleChooseThis', {
                        percent: pkg?.popularPercent || 86,
                      })}
                    </Typography>
                  </div>
                ) : isMobile ? (
                  <></>
                ) : (
                  <div style={{ height: '32px' }} />
                )}
              </div>
            </div>
          </div>
        </StyledNewPackageItem>
      </StyledPackageItemWrapper>
    </>
  );
};

export default BestPackItem;
